import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BbIcon } from '../../components/BbIcon'
import { BbStepper } from '../../components/BbStepper'
import { useModifyWizard } from '../../modify/ModifyWizardContext'
import { KEY_LISTS, TRANSACTION_GROUPS, iconFor } from '../import/steps'
import { MODIFY_STEPS } from './steps'

/* Step 1. The same shape as import step 1 and export step 1 — same entity data,
   same grouping helpers (reused from the import steps module rather than
   duplicated), same Lists / Transactions split. Choosing a record type moves
   straight on to the filters. */
export function ModifySelectTypePage() {
  const wizard = useModifyWizard()
  const navigate = useNavigate()
  const [tab, setTab] = useState<'lists' | 'transactions'>(
    wizard.entity?.isTransaction ? 'transactions' : 'lists',
  )

  // Some registry names are a *kind* of another entity — Sales Tax Items are
  // TaxRates, a credit-card charge is a Purchase. An edit names the record's
  // table directly and has nowhere to carry that kind filter, so QuickBooks
  // rejects it. Offering them here only produces that error after the user has
  // picked records to edit; the server refuses them too, for a stale client.
  const editable = wizard.entities.filter((entity) => entity.canModify)

  const inTab = editable.filter((entity) =>
    tab === 'transactions' ? entity.isTransaction : !entity.isTransaction,
  )

  let groups: Array<{ heading: string; items: typeof inTab }>
  if (tab === 'lists') {
    groups = [
      { heading: 'Key List', items: inTab.filter((e) => KEY_LISTS.includes(e.name)) },
      { heading: 'Other List', items: inTab.filter((e) => !KEY_LISTS.includes(e.name)) },
    ]
  } else {
    const named = new Set(TRANSACTION_GROUPS.flatMap((g) => g.names))
    groups = [
      ...TRANSACTION_GROUPS.map((group) => ({
        heading: group.heading,
        items: inTab.filter((e) => group.names.includes(e.name)),
      })),
      { heading: 'Other', items: inTab.filter((e) => !named.has(e.name)) },
    ]
  }

  const choose = (name: string) => {
    wizard.setEntityType(name)
    navigate('/app/modify/filters')
  }

  return (
    <div className="bb bb-wizard">
      <BbStepper steps={MODIFY_STEPS} current={0} />

      <h2>Select Record Type to Modify</h2>
      <p className="bb-map-help">
        Choose what you want to change, then narrow down which records to edit.
      </p>

      <div className="bb-radios" role="radiogroup" aria-label="Record category">
        <label className="bb-radio">
          <input
            type="radio"
            name="modify_type"
            value="lists"
            checked={tab === 'lists'}
            onChange={() => setTab('lists')}
          />
          Lists
        </label>
        <label className="bb-radio">
          <input
            type="radio"
            name="modify_type"
            value="transactions"
            checked={tab === 'transactions'}
            onChange={() => setTab('transactions')}
          />
          Transactions
        </label>
      </div>

      {groups.map((group) =>
        group.items.length === 0 ? null : (
          <div className="bb-group" key={group.heading}>
            <h3 className="bb-group-h">{group.heading}</h3>
            <div className="bb-types">
              {group.items.map((entity) => (
                <button
                  className={entity.name === wizard.entityType ? 'bb-type bb-type-on' : 'bb-type'}
                  type="button"
                  key={entity.name}
                  aria-pressed={entity.name === wizard.entityType}
                  onClick={() => choose(entity.name)}
                >
                  <span className="bb-type-ico">
                    <BbIcon name={iconFor(entity.name)} />
                  </span>
                  {entity.label ?? entity.name}
                </button>
              ))}
            </div>
          </div>
        ),
      )}

      <div className="bb-wizard-foot">
        <Link className="bb-btn bb-btn-quiet" to="/app">
          Cancel
        </Link>
        {wizard.entityType ? (
          <Link className="bb-btn bb-btn-green" to="/app/modify/filters">
            Next Step
            <BbIcon name="arrow-right" />
          </Link>
        ) : null}
      </div>
    </div>
  )
}
